/**
 * call-api 공통 에러 파싱.
 *
 * studio `src/api/agent/callApiError.ts` 를 admin 콜봇 내선 관리 화면용으로 옮긴 것이다
 * (요청: ~/md/front/admin-콜봇-내선관리-화면-요청.md). 추가 수신자·도구·매뉴얼 등 이 화면이
 * 마주치지 않는 함수·코드는 빼고, 에러 봉투 판독과 문구 우선순위는 원본 그대로 둔다.
 *
 * 각 뷰에 흩어져 있던 parseError 를 한곳으로 모은다. 우선순위:
 * 1. CODE_MESSAGES 에 있는 도메인 코드 — 코드별 고정 안내 문구
 * 2. 404/501 — 백엔드 프록시 미구현 ("준비 중" 안내, 호출부가 문구 지정)
 * 3. call-api 에러 봉투 message
 * 4. fallback
 */

/**
 * @typedef {Object} CallApiErrorBody
 * @property {{ code: string, message: string, details?: unknown }} error call-api 에러 봉투
 */

/**
 * call-api 전역 예외 필터가 "라우트 없음"(엔드포인트 미구현)에 쓰는 코드.
 * 미구현 404 도 정식 봉투({ error: { code: "NOT_FOUND", message: "Cannot GET ..." } })로
 * 감싸져 내려오므로, 봉투 유무가 아니라 이 코드로 미구현을 판정해야 한다.
 */
export const ROUTE_NOT_FOUND_CODE = 'NOT_FOUND';

/**
 * 백엔드 프록시 미구현(라우트 없음 404 또는 501) 여부
 * @param {unknown} err
 * @returns {boolean}
 */
export function isCallApiUnimplemented(err) {
  const status = getCallApiErrorStatus(err);
  if (status === 501) return true;
  if (status !== 404) return false;
  const code = getCallApiErrorCode(err);
  // 봉투 없는 404(프록시 외 인프라) 또는 라우트 없음 코드 — 도메인 404(예:
  // ORGANIZATION_NOT_FOUND, TELEPHONE_NUMBER_NOT_FOUND)는 미구현이 아니다.
  return !code || code === ROUTE_NOT_FOUND_CODE;
}

/**
 * @param {unknown} err
 * @returns {number | undefined}
 */
export function getCallApiErrorStatus(err) {
  return err?.response?.status;
}

/**
 * @param {unknown} err
 * @returns {CallApiErrorBody | undefined}
 */
export function getCallApiErrorBody(err) {
  const body = err?.response?.data;
  // call-api 봉투는 error 가 { code, message } 객체다. NestJS 기본 404 응답
  // ({ message, error: "Not Found", statusCode })의 문자열 error 를 봉투로
  // 오인하면 "미구현"을 "조직 없음"으로 잘못 판정하므로 객체 여부까지 확인한다.
  if (
    body &&
    typeof body === 'object' &&
    'error' in body &&
    typeof body.error === 'object' &&
    body.error !== null
  ) {
    return body;
  }
  return undefined;
}

/**
 * @param {unknown} err
 * @returns {string | undefined}
 */
export function getCallApiErrorCode(err) {
  return getCallApiErrorBody(err)?.error?.code;
}

/**
 * inapi(NestJS 표준) 에러 바디 `{ statusCode, message, error }` 의 message.
 * ValidationPipe 는 message 를 문자열 배열로 주므로 첫 항목을 쓴다.
 * (상담 기록설정처럼 inapi 로 이관된 호출을 같은 파서로 처리하기 위함)
 * @param {unknown} err
 * @returns {string | undefined}
 */
export function getNestErrorMessage(err) {
  const body = err?.response?.data;
  if (!body || typeof body !== 'object' || !('message' in body)) return undefined;
  const m = body.message;
  if (typeof m === 'string') return m;
  if (Array.isArray(m) && typeof m[0] === 'string') return m[0];
  return undefined;
}

/**
 * 도메인 코드 → 한국어 안내 매핑.
 * 봉투 message 원문(영문 가능)을 그대로 노출하지 않기 위한 1차 사전.
 *
 * 내선 관리(7단계) 코드는 서버가 상황별 한국어 message 를 보내므로 여기 넣지 않는다
 * (`PBX_EXTENSION_STATE_CONFLICT` 등 — ~/md/front/studio-내선·전화기·사이트-탭-요청.md 결론 6).
 * 화면 동작이 달라지는 코드는 `pbxErrorReaction` 으로 고른다.
 * `TOKEN_EXPIRED`(401) 는 call-api-axios 인스턴스가 토큰을 갱신해 재시도하므로 문구가 필요 없다.
 */
const CODE_MESSAGES = {
  // 회사 스코프 가드 — 콜봇 전 라우트에 걸리는 공통 거절.
  //
  // admin(CERTILIFE) 은 call-api 의 운영 스코프 예외로 타사 회사 경로를 부른다
  // (~/md/back/콜봇-내선관리-CERTILIFE-운영전용-요청.md). 403 COMPANY_SCOPE_FORBIDDEN 은
  // 없는·삭제된 회사 id(`company_not_found`) 이거나 예외가 아직 배포되지 않은 환경
  // (`cross_company`) 이다. 봉투 message 를 그대로 내보내는 선택지는 없다 —
  // resolveCallApiMessage 는 status 403 을 봉투 한국어 통과보다 먼저 일괄 문구
  // ("요청 권한이 없습니다…")로 잡아채기 때문이다.
  COMPANY_SCOPE_FORBIDDEN:
    '존재하지 않는 병원이거나, 이 환경의 call-api 에 운영(CERTILIFE) 스코프 예외가 아직 배포되지 않았습니다.',
  // 503 이라 봉투 문구도 도달하지만, 서버 문구가 바뀌어도 안내가 흔들리지 않게 고정한다.
  COMPANY_SCOPE_UNAVAILABLE:
    '권한 확인을 일시적으로 할 수 없습니다. 잠시 후 다시 시도해 주세요.',
};

/**
 * @typedef {Object} ParseCallApiErrorOptions
 * @property {string} [unavailableMessage] 404/501(백엔드 프록시 미구현) 시 보여줄 문구. 미지정이면 봉투/fallback 로 진행
 */

/**
 * 호출부용 파서. 모든 에러는 resolveCallApiMessage 가 내는 문구 그대로다.
 * @param {unknown} err
 * @param {string} fallback
 * @param {ParseCallApiErrorOptions} [opts]
 * @returns {string}
 */
export function parseCallApiError(err, fallback, opts = {}) {
  return resolveCallApiMessage(err, fallback, opts);
}

/**
 * @param {unknown} err
 * @param {string} fallback
 * @param {ParseCallApiErrorOptions} opts
 * @returns {string}
 */
function resolveCallApiMessage(err, fallback, opts) {
  const code = getCallApiErrorCode(err);
  if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];
  if (opts.unavailableMessage && isCallApiUnimplemented(err)) {
    return opts.unavailableMessage;
  }

  const status = getCallApiErrorStatus(err);
  // 응답 자체가 없는 axios 에러 — 네트워크 단절 또는 타임아웃 (재시도 안내)
  if (status === undefined && err?.isAxiosError) {
    return err?.code === 'ECONNABORTED'
      ? '요청 시간이 초과됐습니다. 네트워크 상태를 확인하고 잠시 후 다시 시도해주세요.'
      : '서버에 연결하지 못했습니다. 네트워크 상태를 확인하고 잠시 후 다시 시도해주세요.';
  }
  if (status === 401 || status === 403) {
    return '요청 권한이 없습니다. 다시 로그인하거나 관리자에게 문의해주세요.';
  }
  if (status === 429) {
    return '요청이 너무 잦습니다. 잠시 후 다시 시도해주세요.';
  }

  // 봉투 message 는 한국어일 때만 그대로 노출한다 — 영문 원문(vox 패스스루 등)을
  // 사용자에게 보여주지 않는다. (백엔드 한글화 방침: ~/md/back/콜봇-QA-백엔드확인요청.md §4)
  const envelope = getCallApiErrorBody(err)?.error?.message ?? getNestErrorMessage(err);
  if (envelope && /[가-힣]/.test(envelope)) return envelope;
  if (status !== undefined && status >= 500) {
    return '일시적인 서버 오류입니다. 잠시 후 다시 시도해주세요.';
  }
  return code ? `${fallback} (오류 코드: ${code})` : fallback;
}

/**
 * 내선 관리(7단계) 조작 실패 중 **화면 동작이 달라지는** 것 — 문구는 서버 한국어 message 를
 * 그대로 쓰고(CODE_MESSAGES 에 넣지 않는다), 여기서는 다음 동작만 고른다.
 *  - keepModal: MAC 중복 — 등록 모달을 닫지 않고 입력을 고치게 한다
 *  - confirmRecoveryReplace: 유효 복구 코드가 있다 — 재발급 확인 후 `replace: true`
 *  - confirmClaimReplace: 내선에 이미 전화기가 있다 — 교체 확인 후 `replace: true`
 *  - refetch: 대상이 없어졌거나(타사 포함) 전화기가 이미 다른 내선에 붙었다 — 메시지 후 재조회
 * 계약: ~/md/front/studio-내선·전화기·사이트-탭-요청.md 결론 6
 *
 * @typedef {'keepModal' | 'confirmRecoveryReplace' | 'confirmClaimReplace' | 'refetch'} PbxErrorReaction
 */

/**
 * @param {unknown} err
 * @returns {PbxErrorReaction | null}
 */
export function pbxErrorReaction(err) {
  switch (getCallApiErrorCode(err)) {
    case 'PBX_DEVICE_MAC_ALREADY_REGISTERED':
      return 'keepModal';
    case 'PBX_RECOVERY_CODE_ACTIVE':
      return 'confirmRecoveryReplace';
    case 'PBX_EXTENSION_ALREADY_ASSIGNED':
      return 'confirmClaimReplace';
    case 'PBX_DEVICE_ALREADY_ASSIGNED':
    case 'PBX_SITE_NOT_FOUND':
    case 'PBX_DEVICE_NOT_FOUND':
    case 'PBX_EXTENSION_NOT_FOUND':
      return 'refetch';
    default:
      return null;
  }
}
